import * as v from 'valibot'

/**
 * Levels: a player's standing, and what it carries. Every player stands on
 * one — Level 0 when they join — and an administrator raises them. Staff
 * and administrators take no work, so they stand on none. The
 * levels themselves are the studio's data (named, marked, added to in
 * Settings); the attributes a level can carry are code, because what an
 * attribute does is a guarantee the machine keeps.
 *
 * A player sees their own level's name and mark, nothing more: not what it
 * carries, and not that an extra on their statement came from it.
 */

/**
 * What a level can carry. `extra_money` is the amount a new payment sheet
 * starts the player's extra at, in minor units — once per sheet, so once per
 * month, never once per job.
 */
export const LEVEL_ATTRIBUTES = ['extra_money'] as const
export type LevelAttribute = (typeof LEVEL_ATTRIBUTES)[number]

export const LEVEL_ATTRIBUTE_LABEL: Record<LevelAttribute, string> = {
  extra_money: 'Extra money',
}

/** The marks a level may wear, lowest-feeling first. */
export const LEVEL_ICONS = ['sprout', 'star', 'medal', 'award', 'trophy', 'crown', 'gem', 'flame', 'rocket', 'shield', 'zap', 'mountain'] as const
export type LevelIcon = (typeof LEVEL_ICONS)[number]

/** A level as a member wears it — what the player's own page shows. */
export interface LevelBadge {
  id: string
  rank: number
  name: string
  icon: LevelIcon
}

/** A level as the administrator's settings read it. */
export interface Level extends LevelBadge {
  /** Each attribute's amount; null when the level does not carry it. */
  attributes: Record<LevelAttribute, number | null>
  /** How many players stand on it. */
  members: number
  createdAt: string
  updatedAt: string
}

const money = v.pipe(v.number(), v.integer('Money is whole minor units.'), v.minValue(0), v.maxValue(100_000_000))
const name = v.pipe(v.string(), v.trim(), v.minLength(1, 'A level needs a name.'), v.maxLength(40))
const attributes = v.object({ extra_money: v.optional(v.union([money, v.null()])) })

export const createLevelSchema = v.object({
  name,
  icon: v.picklist(LEVEL_ICONS),
  attributes: v.optional(attributes),
})

/** Absent means unchanged; an attribute set to null stops the level carrying it. */
export const updateLevelSchema = v.pipe(
  v.object({
    name: v.optional(name),
    icon: v.optional(v.picklist(LEVEL_ICONS)),
    attributes: v.optional(attributes),
  }),
  v.check((input) => Object.values(input).some((value) => value !== undefined), 'Nothing to change.'),
)

export const setMemberLevelSchema = v.object({ levelId: v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(40)) })

export type CreateLevelInput = v.InferOutput<typeof createLevelSchema>
export type UpdateLevelInput = v.InferOutput<typeof updateLevelSchema>
export type SetMemberLevelInput = v.InferOutput<typeof setMemberLevelSchema>
